import React, { useState, useEffect, useMemo } from "react";
import { useTable, useSortBy } from "react-table";
import { db, storage } from "../Firebase"; // Import Firestore and Storage from Firebase
import { collection, onSnapshot, getDocs } from "firebase/firestore"; // Import Firestore functions
import { ref, getDownloadURL } from "firebase/storage"; // Import Storage functions
import { useNavigate } from "react-router-dom"; // Importing useNavigate for navigation
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import "./Dashboard.css";
import Modal from "react-modal"; // Importing Modal for template selection

// Editable Note Component to handle individual note updates
function EditableNote({ initialValue, onUpdate }) {
  const [value, setValue] = useState(initialValue);

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  const handleBlur = () => {
    if (value !== initialValue) {
      onUpdate(value);
    }
  };

  return (
    <textarea
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      className="note-textarea"
    />
  );
}

function Dashboard() {
  const [data, setData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [addressFilter, setAddressFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state
  const [templates, setTemplates] = useState([]); // State to hold templates
  const [selectedTemplate, setSelectedTemplate] = useState(null); // Selected template

  const navigate = useNavigate(); // To navigate to other pages

  // Fetch data from Firestore when component mounts
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "properties"),
      (snapshot) => {
        const propertiesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setData(propertiesData);
      },
      (error) => {
        console.error("Error fetching properties: ", error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Fetch templates from Firestore
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templatesCollection = collection(db, "templates");
        const templatesSnapshot = await getDocs(templatesCollection);
        const templatesList = templatesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTemplates(templatesList);
      } catch (error) {
        console.error("Error fetching templates: ", error);
      }
    };

    fetchTemplates();
  }, []);

  const handleNoteUpdate = (id, newNote) => {
    setData((oldData) =>
      oldData.map((property) =>
        property.id === id ? { ...property, notes: newNote } : property
      )
    );
  };

  const handleSelectAllChange = () => {
    if (!selectAll) {
      setSelectedProperties(data.map((property) => property));
    } else {
      setSelectedProperties([]);
    }
    setSelectAll(!selectAll);
  };

  const handleCheckboxChange = (property) => {
    setSelectedProperties((prevSelected) =>
      prevSelected.includes(property)
        ? prevSelected.filter((p) => p.id !== property.id)
        : [...prevSelected, property]
    );
  };

  const handleShowAddress = (property) => {
    const fullAddress = `${property.houseNumber}, ${property.roadName}\n${property.town}\n${property.county}\n${property.postcode}`;
    navigator.clipboard.writeText(fullAddress).then(() => {
      alert("Address copied to clipboard!");
    });
  };

  const handleCreateLetters = () => {
    // Open template selection modal
    setIsModalOpen(true);
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setIsModalOpen(false);
    // Proceed with letter creation
    createLetters(template);
  };

  const createLetters = async (template) => {
    if (selectedProperties.length === 0) {
      alert("No properties selected for letter generation.");
      return;
    }
    if (!template) {
      alert("No template selected.");
      return;
    }

    try {
      // Fetch the selected template from storage
      const templateRef = ref(storage, `templates/${template.name}`);
      const url = await getDownloadURL(templateRef);
      const response = await fetch(url);
      const content = await response.arrayBuffer();
      const zip = new PizZip(content);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
      });

      // Generate letters for each selected property
      selectedProperties.forEach((property) => {
        const dynamicData = {
          houseNumber: property.houseNumber || "N/A",
          roadName: property.roadName || "N/A",
          town: property.town || "N/A",
          county: property.county || "N/A",
          postcode: property.postcode || "N/A",
          weeksOnMarket:
            property.weeksOnMarket !== undefined
              ? property.weeksOnMarket
              : "N/A",
          agent: property.agent || "N/A",
        };

        // Set the data for the template
        doc.setData(dynamicData);

        try {
          doc.render();
          const out = doc.getZip().generate({
            type: "blob",
            mimeType:
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });

          // Download the generated letter
          saveAs(out, `${property.roadName}-${property.houseNumber}.docx`);
        } catch (error) {
          console.error("Error rendering document:", error);
          alert(`Failed to generate letter for ${property.roadName}.`);
        }
      });
    } catch (error) {
      console.error("Error fetching template:", error);
      alert("Failed to generate letters. Please try again.");
    }
  };

  const handleUploadTemplate = () => {
    // Navigate to the Upload Template page
    navigate("/upload-template");
  };

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
          document.documentElement.offsetHeight &&
        hasMore
      ) {
        setHasMore(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore]);

  // Address and Agent Options for Filtering
  const roadNames = [...new Set(data.map((property) => property.roadName))];
  const agents = [...new Set(data.map((property) => property.agent))];

  // Filtered Data Based on Address and Agent
  const filteredData = useMemo(() => {
    return data.filter(
      (property) =>
        property.roadName.toLowerCase().includes(addressFilter.toLowerCase()) &&
        property.agent.toLowerCase().includes(agentFilter.toLowerCase())
    );
  }, [data, addressFilter, agentFilter]);

  // Define table columns with sorting capabilities
  const columns = useMemo(
    () => [
      {
        Header: (
          <input
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAllChange}
            className="select-all-checkbox"
          />
        ),
        accessor: "select",
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedProperties.includes(row.original)}
            onChange={() => handleCheckboxChange(row.original)}
          />
        ),
        id: "checkbox",
      },
      {
        Header: "Address",
        accessor: (row) => `${row.houseNumber}, ${row.roadName}`,
        id: "address",
        Cell: ({ row }) => (
          <div className="address-container">
            {`${row.original.houseNumber}, ${row.original.roadName}`}
            <button
              className="show-link"
              onClick={() => handleShowAddress(row.original)}
            >
              Copy
            </button>
          </div>
        ),
      },
      {
        Header: "Asking Price",
        accessor: "askingPrice",
        Cell: ({ value }) => <span>£{value.toLocaleString()}</span>,
        sortType: "basic",
      },
      {
        Header: "Agent",
        accessor: "agent",
      },
      {
        Header: "Weeks on Market",
        accessor: "weeksOnMarket",
        sortType: "basic",
      },
      {
        Header: "Rightmove Link",
        accessor: "rightmoveLink",
        Cell: ({ value }) => (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="link-button"
          >
            View Listing
          </a>
        ),
      },
      {
        Header: "Last Letter Date",
        accessor: "lastLetterDate",
        Cell: ({ value }) => <span>{value}</span>,
        sortType: "basic",
      },
      {
        Header: "Notes",
        accessor: "notes",
        Cell: ({ row }) => {
          const propertyId = row.original.id;
          return (
            <EditableNote
              initialValue={row.original.notes}
              onUpdate={(newNote) => handleNoteUpdate(propertyId, newNote)}
            />
          );
        },
      },
    ],
    [selectAll, selectedProperties, addressFilter, agentFilter]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data: filteredData }, useSortBy);

  return (
    <div className="dashboard-container">
      <h1 className="dashboard-heading">
        Properties on the market in <span className="highlight">Marlow</span>
      </h1>
      <button
        className="upload-template-button large-button"
        onClick={handleUploadTemplate}
      >
        Upload Template
      </button>
      <p className="dashboard-subtext">
        A list of all the properties on the market from me to you
      </p>
      {selectedProperties.length > 0 && (
        <div className="action-button-container show">
          <button className="action-button" onClick={handleCreateLetters}>
            Create Letters
          </button>
          <button
            className="action-button delete-button"
            onClick={() => alert("Delete action selected")}
            style={{ backgroundColor: "red", marginLeft: "20px" }}
          >
            Delete
          </button>
        </div>
      )}
      <table {...getTableProps()} className="property-table">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} className="table-header">
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render("Header")}
                  {column.isSorted ? (
                    column.isSortedDesc ? (
                      <span> 🔽</span>
                    ) : (
                      <span> 🔼</span>
                    )
                  ) : (
                    ""
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} className="table-row">
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {hasMore && <p className="loading-more">Loading more properties...</p>}

      {/* Template Selection Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        contentLabel="Select a Template"
        className="template-modal"
        overlayClassName="modal-overlay"
      >
        <h2>Select a Template for Letters</h2>
        <ul className="template-list">
          {templates.length === 0 ? (
            <li>No templates available. Please upload a template first.</li>
          ) : (
            templates.map((template) => (
              <li
                key={template.id}
                className="template-item"
                onClick={() => handleSelectTemplate(template)}
              >
                {template.name}
              </li>
            ))
          )}
        </ul>
      </Modal>
    </div>
  );
}

export default Dashboard;
